/**
 * TrainingPlans - Admin component for managing customer training plans
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, Search, Dumbbell, User, Calendar, ChevronDown, ChevronUp, 
  Trash2, Edit2, TrendingUp, GripVertical, Image, Save, X, Check,
  FileText, Users, ClipboardList, Sparkles, Video, Play
} from 'lucide-react';
import axios from 'axios';
import TrainingAIAssistant from './training/TrainingAIAssistant';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Exercise categories with icons (including assessment categories)
const CATEGORIES = [
  { id: 'chest', name: 'Brust', icon: '💪' },
  { id: 'back', name: 'Rücken', icon: '🔙' },
  { id: 'legs', name: 'Beine', icon: '🦵' },
  { id: 'shoulders', name: 'Schultern', icon: '🏋️' },
  { id: 'arms', name: 'Arme', icon: '💪' },
  { id: 'core', name: 'Core/Bauch', icon: '🎯' },
  { id: 'cardio', name: 'Cardio', icon: '❤️' },
  { id: 'other', name: 'Sonstiges', icon: '⚡' },
  // Assessment categories
  { id: 'mobility', name: 'Mobilität', icon: '🧘' },
  { id: 'flexibility', name: 'Beweglichkeit', icon: '🤸' },
  { id: 'strength_test', name: 'Kraft-Test', icon: '💪' },
  { id: 'balance', name: 'Balance', icon: '⚖️' },
  { id: 'endurance', name: 'Ausdauer', icon: '🏃' },
];

const TrainingPlans = ({ token }) => {
  const [activeTab, setActiveTab] = useState('plans'); // plans, exercises, templates
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [plans, setPlans] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  
  // Dialog states
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showExerciseDialog, setShowExerciseDialog] = useState(false);
  const [showProgressionDialog, setShowProgressionDialog] = useState(false);
  const [showEntryDialog, setShowEntryDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showEditTemplateDialog, setShowEditTemplateDialog] = useState(false);
  
  // Edit states
  const [editingPlan, setEditingPlan] = useState(null);
  const [editingExercise, setEditingExercise] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [progressionData, setProgressionData] = useState(null);
  const [selectedPlanForEntry, setSelectedPlanForEntry] = useState(null);
  const [selectedExerciseForEntry, setSelectedExerciseForEntry] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Plan form
  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    exercises: []
  });
  
  // Exercise form
  const [exerciseForm, setExerciseForm] = useState({
    name: '',
    category: 'chest',
    description: '',
    equipment: '',
    muscle_groups: [],
    has_height_setting: false
  });
  
  // Entry form
  const [entryForm, setEntryForm] = useState({
    sets: 3,
    reps: 10,
    weight: null,
    height: null,
    notes: ''
  });

  // Template edit form
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    exercises: []
  });

  useEffect(() => {
    loadCustomers();
    loadExercises();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API}/training/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Load templates error:', error);
    }
  };

  useEffect(() => {
    if (selectedCustomer) {
      loadPlans(selectedCustomer.id);
    }
  }, [selectedCustomer]);

  const loadCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Load customers error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExercises = async () => {
    try {
      const response = await axios.get(`${API}/training/exercises`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExercises(response.data);
    } catch (error) {
      console.error('Load exercises error:', error);
    }
  };

  const loadPlans = async (customerId) => {
    try {
      const response = await axios.get(`${API}/training/plans`, {
        params: { customer_id: customerId, include_inactive: true },
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(response.data);
    } catch (error) {
      console.error('Load plans error:', error);
    }
  };

  const loadProgression = async (customerId, exerciseId, exerciseName) => {
    try {
      const response = await axios.get(`${API}/training/entries/progression/${customerId}/${exerciseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgressionData({ ...response.data, exerciseName });
      setShowProgressionDialog(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Progression');
    }
  };

  // ============= TEMPLATE MANAGEMENT =============
  
  const handleEditTemplate = (template) => {
    setTemplateForm({
      name: template.name,
      description: template.description || '',
      exercises: template.exercises.map(ex => ({
        exercise_id: ex.exercise_id,
        exercise: ex.exercise,
        order: ex.order,
        target_sets: ex.target_sets || 3,
        target_reps: ex.target_reps || '8-12',
        target_weight: ex.target_weight,
        target_height: ex.target_height,
        notes: ex.notes || ''
      }))
    });
    setEditingTemplate(template);
    setShowEditTemplateDialog(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim()) {
      toast.error('Bitte einen Namen eingeben');
      return;
    }
    
    try {
      await axios.put(
        `${API}/training/templates/${editingTemplate.id}`,
        {
          name: templateForm.name,
          description: templateForm.description,
          exercises: templateForm.exercises.map((ex, idx) => ({
            exercise_id: ex.exercise_id,
            order: idx,
            target_sets: ex.target_sets || 3,
            target_reps: ex.target_reps || '8-12',
            target_weight: ex.target_weight,
            target_height: ex.target_height,
            notes: ex.notes
          }))
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Vorlage aktualisiert');
      setShowEditTemplateDialog(false);
      setEditingTemplate(null);
      loadTemplates();
    } catch (error) {
      console.error('Save template error:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Vorlage wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/training/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vorlage gelöscht');
      loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const addExerciseToTemplate = (exercise) => {
    setTemplateForm(prev => ({
      ...prev,
      exercises: [...prev.exercises, {
        exercise_id: exercise.id,
        exercise: exercise,
        order: prev.exercises.length,
        target_sets: 3,
        target_reps: '8-12',
        target_weight: null,
        target_height: null,
        notes: ''
      }]
    }));
  };

  const removeExerciseFromTemplate = (index) => {
    setTemplateForm(prev => ({
      ...prev,
      exercises: prev.exercises.filter((_, i) => i !== index)
    }));
  };

  // ============= PLAN MANAGEMENT =============
  
  const handleCreatePlan = () => {
    setPlanForm({ name: '', description: '', exercises: [] });
    setEditingPlan(null);
    setShowPlanDialog(true);
  };

  const handleEditPlan = async (planId) => {
    try {
      const response = await axios.get(`${API}/training/plans/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const plan = response.data;
      setPlanForm({
        name: plan.name,
        description: plan.description || '',
        exercises: plan.exercises.map(ex => ({
          exercise_id: ex.exercise_id,
          exercise: ex.exercise,
          order: ex.order,
          target_sets: ex.target_sets || 3,
          target_reps: ex.target_reps || '8-12',
          target_weight: ex.target_weight,
          target_height: ex.target_height,
          notes: ex.notes
        }))
      });
      setEditingPlan(plan);
      setShowPlanDialog(true);
    } catch (error) {
      toast.error('Fehler beim Laden des Plans');
    }
  };

  const handleSavePlan = async () => {
    if (!planForm.name || planForm.exercises.length === 0) {
      toast.error('Name und mindestens eine Übung erforderlich');
      return;
    }

    try {
      const data = {
        customer_id: selectedCustomer.id,
        name: planForm.name,
        description: planForm.description,
        exercises: planForm.exercises.map((ex, idx) => ({
          exercise_id: ex.exercise_id,
          order: idx + 1,
          target_sets: ex.target_sets,
          target_reps: ex.target_reps,
          target_weight: ex.target_weight,
          target_height: ex.target_height,
          notes: ex.notes
        }))
      };

      if (editingPlan) {
        await axios.put(`${API}/training/plans/${editingPlan.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Plan aktualisiert');
      } else {
        await axios.post(`${API}/training/plans`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Plan erstellt');
      }

      setShowPlanDialog(false);
      loadPlans(selectedCustomer.id);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Plan wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/training/plans/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Plan gelöscht');
      loadPlans(selectedCustomer.id);
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const addExerciseToPlan = (exercise) => {
    setPlanForm(prev => ({
      ...prev,
      exercises: [...prev.exercises, {
        exercise_id: exercise.id,
        exercise: exercise,
        order: prev.exercises.length + 1,
        target_sets: 3,
        target_reps: '8-12',
        target_weight: null,
        target_height: exercise.has_height_setting ? null : undefined,
        notes: ''
      }]
    }));
  };

  const removeExerciseFromPlan = (index) => {
    setPlanForm(prev => ({
      ...prev,
      exercises: prev.exercises.filter((_, i) => i !== index)
    }));
  };

  const updatePlanExercise = (index, field, value) => {
    setPlanForm(prev => ({
      ...prev,
      exercises: prev.exercises.map((ex, i) => 
        i === index ? { ...ex, [field]: value } : ex
      )
    }));
  };

  // ============= EXERCISE MANAGEMENT =============

  const handleCreateExercise = () => {
    setExerciseForm({
      name: '',
      category: 'chest',
      description: '',
      equipment: '',
      muscle_groups: [],
      has_height_setting: false
    });
    setEditingExercise(null);
    setShowExerciseDialog(true);
  };

  const handleEditExercise = (exercise) => {
    setExerciseForm({
      name: exercise.name,
      category: exercise.category,
      description: exercise.description || '',
      equipment: exercise.equipment || '',
      muscle_groups: exercise.muscle_groups || [],
      has_height_setting: exercise.has_height_setting || false
    });
    setEditingExercise(exercise);
    setShowExerciseDialog(true);
  };

  const handleSaveExercise = async () => {
    if (!exerciseForm.name) {
      toast.error('Name erforderlich');
      return;
    }

    try {
      if (editingExercise) {
        await axios.put(`${API}/training/exercises/${editingExercise.id}`, exerciseForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Übung aktualisiert');
      } else {
        await axios.post(`${API}/training/exercises`, exerciseForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Übung erstellt');
      }

      setShowExerciseDialog(false);
      loadExercises();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDeleteExercise = async (exerciseId) => {
    if (!window.confirm('Übung wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/training/exercises/${exerciseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Übung gelöscht');
      loadExercises();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleImageUpload = async (exerciseId, file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/training/exercises/${exerciseId}/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Bild hochgeladen');
      loadExercises();
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    }
  };

  const handleVideoUpload = async (exerciseId, file) => {
    // Validate file type
    if (!file.type.startsWith('video/')) {
      toast.error('Nur Videodateien erlaubt');
      return;
    }
    
    // Validate file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Video zu gross (max. 100MB)');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      toast.info('Video wird hochgeladen...');
      await axios.post(`${API}/training/exercises/${exerciseId}/video`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Video hochgeladen');
      loadExercises();
    } catch (error) {
      toast.error('Fehler beim Video-Upload');
    }
  };

  // ============= ENTRY LOGGING =============

  const handleOpenEntryDialog = (plan, exercise) => {
    setSelectedPlanForEntry(plan);
    setSelectedExerciseForEntry(exercise);
    setEntryForm({
      sets: exercise.target_sets || 3,
      reps: parseInt(exercise.target_reps) || 10,
      weight: exercise.latest_entry?.weight || exercise.target_weight || null,
      height: exercise.latest_entry?.height || exercise.target_height || null,
      notes: ''
    });
    setShowEntryDialog(true);
  };

  const handleSaveEntry = async () => {
    try {
      await axios.post(`${API}/training/entries`, {
        plan_id: selectedPlanForEntry.id,
        exercise_id: selectedExerciseForEntry.exercise_id,
        sets: entryForm.sets,
        reps: entryForm.reps,
        weight: entryForm.weight,
        height: entryForm.height,
        notes: entryForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Eintrag gespeichert');
      setShowEntryDialog(false);
      loadPlans(selectedCustomer.id);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  // Filter exercises
  const filteredExercises = exercises.filter(ex => {
    const matchesSearch = !searchQuery || ex.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !categoryFilter || ex.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 flex items-center justify-center">
          <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Dumbbell className="w-7 h-7 text-green-600" />
            Trainingspläne
          </h1>
          <p className="text-gray-500">Trainingspläne erstellen und Fortschritt tracken</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={activeTab === 'plans' ? 'default' : 'outline'}
          onClick={() => setActiveTab('plans')}
          data-testid="tab-plans"
        >
          <User className="w-4 h-4 mr-2" />
          Kundenpläne
        </Button>
        <Button
          variant={activeTab === 'ai-assistant' ? 'default' : 'outline'}
          onClick={() => setActiveTab('ai-assistant')}
          className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:from-purple-600 hover:to-indigo-700"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          KI-Assistent
        </Button>
        <Button
          variant={activeTab === 'exercises' ? 'default' : 'outline'}
          onClick={() => setActiveTab('exercises')}
          data-testid="tab-exercises"
        >
          <Dumbbell className="w-4 h-4 mr-2" />
          Übungen verwalten
        </Button>
        <Button
          variant={activeTab === 'templates' ? 'default' : 'outline'}
          onClick={() => { setActiveTab('templates'); loadTemplates(); }}
          data-testid="tab-templates"
        >
          <ClipboardList className="w-4 h-4 mr-2" />
          Vorlagen
        </Button>
      </div>

      {/* AI Assistant Tab */}
      {activeTab === 'ai-assistant' && (
        <TrainingAIAssistant 
          token={token} 
          customers={customers}
          onPlanCreated={loadPlans}
        />
      )}

      {/* Plans Tab */}
      {activeTab === 'plans' && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Customer List */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg">Kunden</CardTitle>
              <Input
                placeholder="Kunde suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="mt-2"
              />
            </CardHeader>
            <CardContent className="max-h-[500px] overflow-y-auto">
              <div className="space-y-2">
                {customers
                  .filter(c => !searchQuery || 
                    `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    c.email?.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map(customer => (
                    <div
                      key={customer.id}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedCustomer?.id === customer.id 
                          ? 'bg-green-100 border-2 border-green-500' 
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      onClick={() => setSelectedCustomer(customer)}
                    >
                      <p className="font-medium">{customer.first_name} {customer.last_name}</p>
                      <p className="text-xs text-gray-500">{customer.email}</p>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>

          {/* Plans for selected customer */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">
                    {selectedCustomer 
                      ? `Pläne für ${selectedCustomer.first_name} ${selectedCustomer.last_name}`
                      : 'Kunde auswählen'
                    }
                  </CardTitle>
                  {selectedCustomer && (
                    <CardDescription>{plans.length} Plan(e)</CardDescription>
                  )}
                </div>
                {selectedCustomer && (
                  <Button onClick={handleCreatePlan} className="bg-green-600 hover:bg-green-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Neuer Plan
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!selectedCustomer ? (
                <div className="text-center py-12 text-gray-500">
                  <User className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p>Wählen Sie einen Kunden aus der Liste</p>
                </div>
              ) : plans.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Dumbbell className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p>Noch keine Trainingspläne</p>
                  <Button onClick={handleCreatePlan} className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Ersten Plan erstellen
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {plans.map((plan, planIndex) => (
                    <PlanCard
                      key={plan.id}
                      plan={plan}
                      isActive={plan.is_active}
                      onEdit={() => handleEditPlan(plan.id)}
                      onDelete={() => handleDeletePlan(plan.id)}
                      onLogEntry={(exercise) => handleOpenEntryDialog(plan, exercise)}
                      onViewProgression={(exercise) => loadProgression(selectedCustomer.id, exercise.exercise_id, exercise.exercise?.name)}
                      defaultExpanded={planIndex === 0}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Exercises Tab */}
      {activeTab === 'exercises' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Übungsdatenbank</CardTitle>
              <Button onClick={handleCreateExercise} className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Neue Übung
              </Button>
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              <Input
                placeholder="Übung suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-xs"
              />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="border rounded-md px-3 py-2"
              >
                <option value="">Alle Kategorien</option>
                {CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                ))}
              </select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredExercises.map(exercise => (
                <div
                  key={exercise.id}
                  className="p-3 border rounded-lg hover:border-green-300 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    {exercise.image_url ? (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${exercise.image_url}`} 
                        alt={exercise.name}
                        className="w-16 h-16 rounded object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded bg-gray-100 flex items-center justify-center">
                        <Dumbbell className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{exercise.name}</p>
                      <div className="flex gap-1 mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs">
                          {CATEGORIES.find(c => c.id === exercise.category)?.name || exercise.category}
                        </Badge>
                        {exercise.has_height_setting && (
                          <Badge variant="secondary" className="text-xs">Höhe</Badge>
                        )}
                        {exercise.video_url && (
                          <Badge className="text-xs bg-blue-500">
                            <Play className="w-3 h-3 mr-1" />
                            Video
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditExercise(exercise)}
                      title="Bearbeiten"
                    >
                      <Edit2 className="w-3 h-3" />
                    </Button>
                    <label className="cursor-pointer">
                      <Button variant="ghost" size="sm" asChild title="Bild hochladen">
                        <span><Image className="w-3 h-3" /></span>
                      </Button>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && handleImageUpload(exercise.id, e.target.files[0])}
                      />
                    </label>
                    <label className="cursor-pointer">
                      <Button variant="ghost" size="sm" asChild title="Video hochladen">
                        <span><Video className="w-3 h-3 text-blue-600" /></span>
                      </Button>
                      <input
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && handleVideoUpload(exercise.id, e.target.files[0])}
                      />
                    </label>
                    {exercise.video_url && (
                      <a 
                        href={`${process.env.REACT_APP_BACKEND_URL}${exercise.video_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center h-8 px-2 text-blue-600 hover:bg-blue-50 rounded"
                        title="Video ansehen"
                      >
                        <Play className="w-3 h-3" />
                      </a>
                    )}
                    {!exercise.is_default && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteExercise(exercise.id)}
                        className="text-red-500"
                        title="Löschen"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-6">
          {/* Assessment Templates */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-blue-600" />
                    Fitness Assessment Vorlage
                  </CardTitle>
                  <CardDescription>Standard-Vorlage für die Ersteinschätzung neuer Kunden</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Keine Vorlagen verfügbar</p>
                  <p className="text-sm mt-1">Die Standard-Assessment-Vorlage wird beim ersten Laden erstellt.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {templates.map(template => (
                    <div key={template.id} className="border rounded-lg p-4 bg-blue-50/50">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-lg flex items-center gap-2">
                            {template.name}
                            {template.is_default_template && (
                              <Badge className="bg-blue-600">Standard</Badge>
                            )}
                            {template.type === 'assessment' && (
                              <Badge variant="outline" className="text-blue-600 border-blue-300">Assessment</Badge>
                            )}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                          <p className="text-xs text-gray-500 mt-2">{template.exercises?.length || 0} Übungen</p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline"
                            onClick={() => handleEditTemplate(template)}
                            className="border-blue-300 text-blue-700 hover:bg-blue-50"
                          >
                            <Edit2 className="w-4 h-4 mr-1" />
                            Bearbeiten
                          </Button>
                          <Button 
                            onClick={() => { setSelectedTemplate(template); setShowAssignDialog(true); }}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid="assign-template-btn"
                          >
                            <Users className="w-4 h-4 mr-2" />
                            Zuweisen
                          </Button>
                          {!template.is_default_template && (
                            <Button 
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteTemplate(template.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Exercise preview - collapsible */}
                      <details className="mt-3">
                        <summary className="cursor-pointer text-sm font-medium text-blue-700 hover:text-blue-800">
                          Übungen anzeigen ({template.exercises?.length || 0})
                        </summary>
                        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {template.exercises?.map((ex, idx) => (
                            <div key={idx} className="p-2 bg-white rounded border text-sm">
                              <span className="w-5 h-5 inline-flex items-center justify-center rounded-full bg-gray-100 text-xs mr-2">
                                {idx + 1}
                              </span>
                              <span className="font-medium">{ex.exercise?.name}</span>
                              <span className="text-gray-500 text-xs block ml-7">
                                {CATEGORIES.find(c => c.id === ex.exercise?.category)?.name || ex.exercise?.category}
                              </span>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Plan Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPlan ? 'Plan bearbeiten' : 'Neuer Trainingsplan'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Planname</Label>
                <Input
                  value={planForm.name}
                  onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                  placeholder="z.B. Ganzkörper Plan A"
                />
              </div>
              <div>
                <Label>Beschreibung (optional)</Label>
                <Input
                  value={planForm.description}
                  onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                  placeholder="z.B. Fokus auf Kraft"
                />
              </div>
            </div>

            {/* Exercise Selection */}
            <div>
              <Label className="mb-2 block">Übungen hinzufügen</Label>
              <div className="flex gap-2 mb-3">
                <Input
                  placeholder="Übung suchen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1"
                />
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="border rounded-md px-3"
                >
                  <option value="">Alle</option>
                  {CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                {filteredExercises.slice(0, 20).map(ex => (
                  <div
                    key={ex.id}
                    className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer"
                    onClick={() => addExerciseToPlan(ex)}
                  >
                    <span className="text-sm">{ex.name}</span>
                    <Plus className="w-4 h-4 text-green-600" />
                  </div>
                ))}
              </div>
            </div>

            {/* Selected Exercises */}
            <div>
              <Label className="mb-2 block">Übungen im Plan ({planForm.exercises.length})</Label>
              {planForm.exercises.length === 0 ? (
                <p className="text-gray-500 text-sm py-4 text-center">Noch keine Übungen ausgewählt</p>
              ) : (
                <div className="space-y-3">
                  {planForm.exercises.map((ex, index) => (
                    <div key={index} className="p-3 border rounded-lg bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <GripVertical className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{ex.exercise?.name}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeExerciseFromPlan(index)}
                          className="text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <Label className="text-xs">Sets</Label>
                          <Input
                            type="number"
                            value={ex.target_sets || ''}
                            onChange={(e) => updatePlanExercise(index, 'target_sets', parseInt(e.target.value))}
                            placeholder="3"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Reps</Label>
                          <Input
                            value={ex.target_reps || ''}
                            onChange={(e) => updatePlanExercise(index, 'target_reps', e.target.value)}
                            placeholder="8-12"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Gewicht (kg)</Label>
                          <Input
                            type="number"
                            value={ex.target_weight || ''}
                            onChange={(e) => updatePlanExercise(index, 'target_weight', parseFloat(e.target.value) || null)}
                            placeholder="-"
                          />
                        </div>
                        {ex.exercise?.has_height_setting && (
                          <div>
                            <Label className="text-xs">Höhe</Label>
                            <Input
                              type="number"
                              value={ex.target_height || ''}
                              onChange={(e) => updatePlanExercise(index, 'target_height', parseInt(e.target.value) || null)}
                              placeholder="-"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowPlanDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSavePlan} className="bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" />
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Exercise Dialog */}
      <Dialog open={showExerciseDialog} onOpenChange={setShowExerciseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingExercise ? 'Übung bearbeiten' : 'Neue Übung'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={exerciseForm.name}
                onChange={(e) => setExerciseForm({ ...exerciseForm, name: e.target.value })}
                placeholder="z.B. Bankdrücken"
              />
            </div>
            <div>
              <Label>Kategorie</Label>
              <select
                value={exerciseForm.category}
                onChange={(e) => setExerciseForm({ ...exerciseForm, category: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Equipment</Label>
              <select
                value={exerciseForm.equipment}
                onChange={(e) => setExerciseForm({ ...exerciseForm, equipment: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="">Keins</option>
                <option value="barbell">Langhantel</option>
                <option value="dumbbell">Kurzhantel</option>
                <option value="machine">Maschine</option>
                <option value="cable">Kabelzug</option>
                <option value="bodyweight">Körpergewicht</option>
                <option value="other">Sonstiges</option>
              </select>
            </div>
            <div>
              <Label>Beschreibung</Label>
              <Input
                value={exerciseForm.description}
                onChange={(e) => setExerciseForm({ ...exerciseForm, description: e.target.value })}
                placeholder="Ausführungshinweise..."
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="has_height"
                checked={exerciseForm.has_height_setting}
                onChange={(e) => setExerciseForm({ ...exerciseForm, has_height_setting: e.target.checked })}
              />
              <Label htmlFor="has_height">Hat Höheneinstellung (Rack, Kabelzug, etc.)</Label>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowExerciseDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSaveExercise} className="bg-green-600 hover:bg-green-700">
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Entry Dialog */}
      <Dialog open={showEntryDialog} onOpenChange={setShowEntryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Training eintragen: {selectedExerciseForEntry?.exercise?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sets</Label>
                <Input
                  type="number"
                  value={entryForm.sets}
                  onChange={(e) => setEntryForm({ ...entryForm, sets: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label>Reps</Label>
                <Input
                  type="number"
                  value={entryForm.reps}
                  onChange={(e) => setEntryForm({ ...entryForm, reps: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Gewicht (kg)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={entryForm.weight || ''}
                  onChange={(e) => setEntryForm({ ...entryForm, weight: parseFloat(e.target.value) || null })}
                  placeholder="z.B. 60"
                />
              </div>
              {selectedExerciseForEntry?.exercise?.has_height_setting && (
                <div>
                  <Label>Höhe</Label>
                  <Input
                    type="number"
                    value={entryForm.height || ''}
                    onChange={(e) => setEntryForm({ ...entryForm, height: parseInt(e.target.value) || null })}
                    placeholder="z.B. 5"
                  />
                </div>
              )}
            </div>
            <div>
              <Label>Notizen</Label>
              <Input
                value={entryForm.notes}
                onChange={(e) => setEntryForm({ ...entryForm, notes: e.target.value })}
                placeholder="z.B. Letzte Rep war schwer"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEntryDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSaveEntry} className="bg-green-600 hover:bg-green-700">
                <Check className="w-4 h-4 mr-2" />
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Progression Dialog */}
      <Dialog open={showProgressionDialog} onOpenChange={setShowProgressionDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Progression: {progressionData?.exerciseName}
            </DialogTitle>
          </DialogHeader>
          
          {progressionData && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">{progressionData.total_entries} Einträge</p>
              
              {progressionData.entries.length === 0 ? (
                <p className="text-center py-8 text-gray-500">Noch keine Einträge</p>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2">Datum</th>
                        <th className="text-center p-2">Sets</th>
                        <th className="text-center p-2">Reps</th>
                        <th className="text-center p-2">Gewicht</th>
                        {progressionData.exercise?.has_height_setting && (
                          <th className="text-center p-2">Höhe</th>
                        )}
                        <th className="text-left p-2">Notizen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {progressionData.entries.map((entry, idx) => {
                        const prevEntry = idx > 0 ? progressionData.entries[idx - 1] : null;
                        const weightChange = prevEntry && entry.weight && prevEntry.weight 
                          ? entry.weight - prevEntry.weight 
                          : 0;
                        
                        return (
                          <tr key={entry.id} className="border-b">
                            <td className="p-2">
                              {new Date(entry.date).toLocaleDateString('de-CH')}
                            </td>
                            <td className="text-center p-2">{entry.sets}</td>
                            <td className="text-center p-2">{entry.reps}</td>
                            <td className="text-center p-2">
                              {entry.weight ? (
                                <span className="flex items-center justify-center gap-1">
                                  {entry.weight}kg
                                  {weightChange > 0 && (
                                    <span className="text-green-600 text-xs">+{weightChange}</span>
                                  )}
                                  {weightChange < 0 && (
                                    <span className="text-red-600 text-xs">{weightChange}</span>
                                  )}
                                </span>
                              ) : '-'}
                            </td>
                            {progressionData.exercise?.has_height_setting && (
                              <td className="text-center p-2">{entry.height || '-'}</td>
                            )}
                            <td className="p-2 text-gray-500 text-xs">{entry.notes || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Template Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-green-600" />
              Vorlage zuweisen: {selectedTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Wählen Sie einen Kunden aus, dem die Vorlage &quot;{selectedTemplate?.name}&quot; zugewiesen werden soll.
            </p>
            
            <div>
              <Label>Kunde auswählen</Label>
              <Input
                placeholder="Kunde suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="mt-2"
                data-testid="assign-customer-search"
              />
            </div>
            
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              {customers
                .filter(c => !searchQuery || 
                  `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  c.email?.toLowerCase().includes(searchQuery.toLowerCase())
                )
                .map(customer => (
                  <div
                    key={customer.id}
                    className="p-3 hover:bg-green-50 cursor-pointer border-b last:border-b-0 flex items-center justify-between"
                    onClick={async () => {
                      try {
                        await axios.post(
                          `${API}/training/templates/${selectedTemplate.id}/assign/${customer.id}`,
                          {},
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        toast.success(`"${selectedTemplate.name}" an ${customer.first_name} ${customer.last_name} zugewiesen`);
                        setShowAssignDialog(false);
                        setSearchQuery('');
                        // If customer is selected, reload their plans
                        if (selectedCustomer?.id === customer.id) {
                          loadPlans(customer.id);
                        }
                      } catch (error) {
                        if (error.response?.status === 400) {
                          toast.error('Kunde hat diese Vorlage bereits');
                        } else {
                          toast.error('Fehler beim Zuweisen');
                        }
                      }
                    }}
                    data-testid={`assign-customer-${customer.id}`}
                  >
                    <div>
                      <p className="font-medium">{customer.first_name} {customer.last_name}</p>
                      <p className="text-xs text-gray-500">{customer.email}</p>
                    </div>
                    <Plus className="w-5 h-5 text-green-600" />
                  </div>
                ))
              }
            </div>
            
            <div className="flex justify-between pt-2 border-t">
              <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
                Abbrechen
              </Button>
              <Button
                onClick={async () => {
                  if (!selectedTemplate) return;
                  try {
                    const response = await axios.post(
                      `${API}/training/templates/${selectedTemplate.id}/assign-all`,
                      {},
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    toast.success(`Vorlage an ${response.data.assigned_count} Kunden zugewiesen (${response.data.skipped_count} übersprungen)`);
                    setShowAssignDialog(false);
                  } catch (error) {
                    toast.error('Fehler beim Zuweisen');
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="assign-all-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                An alle Kunden zuweisen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={showEditTemplateDialog} onOpenChange={setShowEditTemplateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5" />
              Vorlage bearbeiten
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vorlagenname *</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="z.B. Fitness Assessment"
                />
              </div>
              <div>
                <Label>Beschreibung</Label>
                <Input
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                  placeholder="z.B. Standard-Assessment für neue Kunden"
                />
              </div>
            </div>

            {/* Exercise List */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold">Übungen ({templateForm.exercises.length})</Label>
              </div>

              {templateForm.exercises.length === 0 ? (
                <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed">
                  <Dumbbell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-gray-500 text-sm">Keine Übungen ausgewählt</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {templateForm.exercises.map((ex, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                        {idx + 1}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{ex.exercise?.name}</p>
                        <p className="text-xs text-gray-500">
                          {CATEGORIES.find(c => c.id === ex.exercise?.category)?.name || ex.exercise?.category}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          className="w-16 text-center text-sm"
                          value={ex.target_sets}
                          onChange={(e) => {
                            const newExercises = [...templateForm.exercises];
                            newExercises[idx].target_sets = parseInt(e.target.value) || 3;
                            setTemplateForm({ ...templateForm, exercises: newExercises });
                          }}
                          placeholder="Sets"
                        />
                        <span className="text-gray-400">x</span>
                        <Input
                          className="w-20 text-center text-sm"
                          value={ex.target_reps}
                          onChange={(e) => {
                            const newExercises = [...templateForm.exercises];
                            newExercises[idx].target_reps = e.target.value;
                            setTemplateForm({ ...templateForm, exercises: newExercises });
                          }}
                          placeholder="Reps"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeExerciseFromTemplate(idx)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add Exercise Section */}
            <div>
              <Label className="text-sm text-gray-600 mb-2 block">Übung hinzufügen</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 bg-gray-50 rounded-lg">
                {exercises
                  .filter(ex => !templateForm.exercises.find(te => te.exercise_id === ex.id))
                  .map(exercise => (
                    <button
                      key={exercise.id}
                      onClick={() => addExerciseToTemplate(exercise)}
                      className="p-2 text-left bg-white rounded border hover:border-blue-400 hover:bg-blue-50 transition-colors"
                    >
                      <p className="text-sm font-medium truncate">{exercise.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {CATEGORIES.find(c => c.id === exercise.category)?.name || exercise.category}
                      </p>
                    </button>
                  ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowEditTemplateDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={handleSaveTemplate}
                className="bg-blue-600 hover:bg-blue-700"
                disabled={!templateForm.name.trim()}
              >
                <Save className="w-4 h-4 mr-2" />
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Plan Card Component
const PlanCard = ({ plan, isActive, onEdit, onDelete, onLogEntry, onViewProgression, defaultExpanded }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Card className={`${isActive ? 'border-green-300 bg-green-50/30' : 'border-gray-200 bg-gray-50'}`}>
      <CardHeader className="py-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                {plan.name}
                {isActive && <Badge className="bg-green-600 text-xs">Aktiv</Badge>}
                {!isActive && <Badge variant="secondary" className="text-xs">Archiviert</Badge>}
              </CardTitle>
              <CardDescription className="text-xs">
                Erstellt: {new Date(plan.created_at).toLocaleDateString('de-CH')} • {plan.exercises?.length || 0} Übungen • {plan.entry_count || 0} Einträge
              </CardDescription>
            </div>
          </div>
          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="sm" onClick={onEdit}>
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-500">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      {expanded && (
        <CardContent className="pt-0">
          <div className="space-y-2">
            {plan.exercises?.map((ex, idx) => (
              <div 
                key={idx}
                className="flex items-center justify-between p-3 bg-white rounded-lg border"
              >
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium">
                    {idx + 1}
                  </span>
                  <div>
                    <p className="font-medium text-sm">{ex.exercise?.name}</p>
                    <p className="text-xs text-gray-500">
                      {ex.target_sets} × {ex.target_reps}
                      {ex.target_weight && ` @ ${ex.target_weight}kg`}
                      {ex.target_height && ` (Höhe: ${ex.target_height})`}
                    </p>
                    {ex.latest_entry && (
                      <p className="text-xs text-green-600 mt-1">
                        Letztes Training: {ex.latest_entry.sets}×{ex.latest_entry.reps}
                        {ex.latest_entry.weight && ` @ ${ex.latest_entry.weight}kg`}
                        {' '}({new Date(ex.latest_entry.date).toLocaleDateString('de-CH')})
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewProgression(ex)}
                    title="Progression anzeigen"
                  >
                    <TrendingUp className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => onLogEntry(ex)}
                    className="bg-green-600 hover:bg-green-700"
                    title="Training eintragen"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default TrainingPlans;
